import { useSessionStore } from "@auth/sessionStore";
import { useQueryClient } from "@tanstack/react-query";
import type { TodayVisit } from "@visits/visitsClient";
import { visitsKeys } from "@visits/visitsQueries";
import { occupantsOf } from "@workspace/useRoomOccupants";
import { useWorkspaceNavigation } from "@workspace/useWorkspaceNavigation";
import { useCallback } from "react";

/**
 * Opening a room opens its earliest-scheduled patient (WEB-3288), keeping the room
 * in the URL as context: `?room=X&visit=Y`. The one place that rule lives, since the
 * clinic tile and search must agree; an empty room falls back to the room.
 * Reads today's visits from the cache, since callers are click handlers.
 */
export const useOpenRoom = () => {
  const queryClient = useQueryClient();
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { showRoom, selectVisit } = useWorkspaceNavigation();

  return useCallback(
    (roomUuid: string) => {
      const visits =
        queryClient.getQueryData<TodayVisit[]>(visitsKeys.today(sessionKey)) ??
        [];
      const entry = occupantsOf(visits, roomUuid)[0];
      if (entry) selectVisit(entry.id, roomUuid);
      else showRoom(roomUuid);
    },
    [queryClient, sessionKey, showRoom, selectVisit],
  );
};
